import React, { useState } from "react";
import { Button, TextField, Typography, Box, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../components/SnackBarProvider"; // Import your Snackbar hook
import axiosInstance from "../../components/AxiosInstance"; // Import Axios instance
import { API_BASE_URL } from "../../constants";
import { Link } from "react-router-dom"; // ✅ Import Link from react-router-dom

const EmailInput = () => {
  const [email, setEmail] = useState(""); // State to track email input
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // State to disable the Next button during API call
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar(); // Destructure `openSnackbar` from your custom hook

  const fetchPhoneForEmail = async (email) => {
    console.log("Fetching phone number for email:", email);
    try {
      const res = await axiosInstance.post(
        `${API_BASE_URL}/api/getphoneforemail`, // API Endpoint
        { email } // Payload
      );

      if (res.status === 200) {
        return res.data; // Return the phone number from the API response
      } else {
        throw new Error("Failed to fetch phone number");
      }
    } catch (error) {
      console.error("Error fetching phone number:", error);
      throw error; // Throw error for handling in the parent function
    }
  };

  const handleNextClick = async () => {
    if (!email) {
      openSnackbar("Email is required.", "error"); // Show error snackbar if email is empty
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email validation regex
    if (!emailRegex.test(email)) {
      openSnackbar("Please enter a valid email address.", "error"); // Show error snackbar if email is invalid
      return;
    }

    setIsButtonDisabled(true); // Disable the button while the API call is in progress

    try {
      const response = await fetchPhoneForEmail(email);
      console.log("Fetched response:", response);

      // 🛠 Ensure `proceed` is a boolean by using JSON.parse() OR checking explicitly
      const proceed = response.proceed === true || response.proceed === "true";

      if (proceed) {
        // ✅ Navigate only if `proceed` is true
        navigate("/signup", { state: { email, phoneNumber: response.phone } });
      } else {
        // ❌ Stay on the page and show an error
        openSnackbar("Email already exists. Redirecting to login...", "error");
        setTimeout(() => navigate("/"), 3000); // ✅ Redirect after 2 seconds
      }
    } catch (error) {
      openSnackbar("Failed to fetch phone number. Please try again.", "error");
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 12,
          marginLeft: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#87CEFA",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          width: "auto",
          minWidth: "350px",
          maxWidth: "400px",
          height: "350px",
        }}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleNextClick();
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{ color: "#000" }}
        >
          Sign Up
        </Typography>

        {/* Email Label */}
        <Typography
          variant="body1"
          sx={{
            color: "#000",
            fontSize: "0.8rem",
            fontWeight: "500",
            mb: -1.5,
            letterSpacing: "0.05em",
            alignSelf: "flex-start",
          }}
        >
          Email <span style={{ color: "red" }}>*</span>
        </Typography>

        {/* Email Input Field */}
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          sx={{
            backgroundColor: "#fff",
            borderRadius: "4px",
          }}
        />

        {/* Next Button */}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isButtonDisabled}
          sx={{
            mt: 3,
            mb: 2,
            backgroundColor: isButtonDisabled ? "#6c757d" : "#007bff",
            "&:hover": isButtonDisabled ? {} : { backgroundColor: "#0056b3" },
            width: "130px",
            height: "35px",
            fontSize: "13px",
            padding: "8px 12px",
            borderRadius: "40px",
          }}
        >
          {isButtonDisabled ? "Processing..." : "Next"}
        </Button>

        {/* Login Link */}
        <Typography
          sx={{
            mt: 3,
            fontSize: "1.1rem",
            fontWeight: "600", // ✅ Slightly bolder for emphasis
            textAlign: "center",
            color: "#1a1a1a", // ✅ Dark Gray (Better contrast)
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration: "none",
              color: "#1565C0", // ✅ Deep Blue (Professional contrast)
              fontWeight: "bold",
              transition: "color 0.3s ease-in-out", // Smooth color transition effect
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px", // ✅ Spacing between icon & text
            }}
            onMouseEnter={(e) => (e.target.style.color = "#0d47a1")} // Darker blue on hover
            onMouseLeave={(e) => (e.target.style.color = "#1565C0")} // Restore original color
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px", // ✅ Ensures a proper circular shape
                height: "20px",
                borderRadius: "50%", // ✅ Makes it a perfect circle
                backgroundColor: "#00C853", // ✅ Green background
                color: "white", // ✅ White tick color for contrast
                fontSize: "1rem",
                fontWeight: "bold",
                marginBottom:"2px"
              }}
            >
              ✔
            </span>
            Already have an account?
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default EmailInput;
